import * as React from "react";
import * as moment from "moment";
import {RouteComponentProps} from "react-router-dom";
import {
    ICacheManager,
    ICustomerPortalService,
    Party,
    Policy,
    productHelper,
    resourcesHelper,
    Types,
    INTERACTION_PROCESS_NB,
    PRODUCT_ANNUAL_HOME,
    PRODUCT_MOTOR,
    PRODUCT_RENTERS,
    PRODUCT_WOLF_MOTOR, PolicyPropertyEnum, PolicyProperty
} from "@falcon/services";
import {authorize, Overlay} from "@athena-portal/components";
import {lazyInject} from "@athena-portal/core";
import {PluginTemplates} from "../../../utils/PluginTemplates";
import FalconHeader from "../../../components/navigation/FalconHeader";
import FalconFooter from "../../../components/navigation/FalconFooter";
import {capitalize, formatDateTime} from "../../../CommonFunctions";
import {ImgTileLink} from "../../../components/ImgTileLink";
import {Redirect} from "react-router";
import {ButtonLink} from "../../../components/navigation/ButtonLink";
import {OverdueInstalmentBanner} from "../../../components/banners/OverdueInstalmentBanner";
import {OtherMissedPaymentBanner} from "../../../components/banners/OtherMissedPaymentBanner";
import {UpdateOtherPolicesBanner} from "../annual/motor/common/UpdateOtherPolicesBanner";
import {maybePushPurchaseEvent} from "./purchaseEvent";
import {LVDataLayerHelper} from "../../../utils/LVDataLayerHelper";
import {QuickQuoteBanner} from "../../../components/banners/QuickQuoteBanner";
import {pushToAdobeDataLayerDelayed} from "../../../utils/AdobeDataLayerHelper";

interface IProps extends RouteComponentProps<{}> {
}

interface IState {
    policies?: Policy[];
    expiredPolicies?: Policy[];
    hasExpiredAnnualMotorPolicies?: boolean;
    policiesProcessed: boolean;
    party?: Party;
    partyLoaded: boolean;
    multiPolicyAccount: boolean;
}

@authorize
export class MultiPoliciesDashboardPage extends React.Component<IProps, IState> {

    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    @lazyInject(Types.CacheManager)
    private cacheManager: ICacheManager;
    private pushToAdobeDataLayerTimeout: any;

    constructor(props: IProps) {
        super(props);

        this.state = {
            policiesProcessed: false,
            partyLoaded: false,
            multiPolicyAccount: false
        };
    }

    public componentDidMount(): void {
        this.pluginTemplates.setPageTitle("Dashboard");

        window.sessionStorage.removeItem("product");
        window.sessionStorage.removeItem("payment");

        if (window.sessionStorage.getItem("referrerJourney") === "nb") {
            this.cacheManager.clearCache("getPolicies");
        }

        this.customerPortalService.getUserPoliciesWithLatestVersions().subscribe((policies: Policy[]) => {
            const referrerJourney = window.sessionStorage.getItem("referrerJourney");
            const showSuccessPopup = window.sessionStorage.getItem("showPopup");
            if (showSuccessPopup && referrerJourney === INTERACTION_PROCESS_NB) {
                const latestPolicy = this.getLatestPolicy(policies);
                const interaction = latestPolicy.interaction;
                new LVDataLayerHelper().mapInteractionToDataLayer(interaction);
                maybePushPurchaseEvent(window.LvDataLayer.policyReference);
            }

            if (policies.length > 0) {
                this.customerPortalService.getProfile().subscribe((party: Party) => {
                    this.setState({
                        party,
                        partyLoaded: true
                    });
                })
            } else {
                this.setState({
                    partyLoaded: true
                })
            }

            if (policies.length > 1 && !productHelper.includesOnlyMonthlySubscriptionProduct(policies)) {
                window.sessionStorage.setItem("multiPolicyAccount", "true");
                this.setState({
                    multiPolicyAccount: true
                })
            }

            this.setState({
                policies,
                policiesProcessed: true
            });
        });

        // TODO: probably need some further refactoring to select all policies at once and filter expired ones when required
        this.customerPortalService.getExpiredPolicies().subscribe(policies => {
            this.setState({
                hasExpiredAnnualMotorPolicies: Boolean(policies && policies.length > 0),
                expiredPolicies: policies
            });
        });
        this.pushToAdobeDataLayerTimeout = pushToAdobeDataLayerDelayed(2000);
    }

    componentWillUnmount() {
        clearTimeout(this.pushToAdobeDataLayerTimeout);
    }

    public render(): React.ReactNode {
        const {policies, hasExpiredAnnualMotorPolicies} = this.state;
        const isDataLoaded = this.isDataLoaded();
        const hasPolicies = this.hasPolicies();

        if (policies) {
            const onlyAnnualMotorPolicyPresent = productHelper.includesOnlyWolfMotorProduct(policies) || (policies.length === 0 && hasExpiredAnnualMotorPolicies);
            const onlyMonthlySubscriptionPolicyPresent = productHelper.includesOnlyMonthlySubscriptionProduct(policies);

            if (onlyAnnualMotorPolicyPresent && policies.length === 1) {
                return this.redirectToSingleProductDashboard(`/dashboard/annualMotor/${policies[0].policyNumber}`);
            } else if (onlyMonthlySubscriptionPolicyPresent) {
                return this.redirectToSingleProductDashboard("/dashboard/monthlySubscription");
            } else if (policies.length === 1 && productHelper.includesAnnualHomeProduct(policies)) {
                return this.redirectToSingleProductDashboard(`/dashboard/annualHome/${policies[0].policyNumber}`);
            }
        }

        return (
            <React.Fragment>
                <Overlay show={!isDataLoaded}/>
                <FalconHeader/>
                <div className="page-contents dashboard-page">
                    <UpdateOtherPolicesBanner/>
                    <h1 className="page-heading-white">{this.state.party ? `Hi ${this.state.party.firstName}` : ""}</h1>
                    {isDataLoaded && (hasPolicies
                        ? this.renderPageContents()
                        : this.renderNewClientPageContents())}
                </div>
                <FalconFooter/>
            </React.Fragment>
        );
    }

    private renderPageContents(): JSX.Element {
        return (
            <React.Fragment>
                {this.renderPolicyGrid()}
            </React.Fragment>
        );
    }

    private getLatestPolicy(policies: Policy[]): Policy {
        return policies.reduce((latest, current) => {
            const latestDate = moment(latest.interaction.createdOn);
            const currentDate = moment(current.interaction.createdOn);
            return currentDate.isAfter(latestDate) ? current : latest;
        }, policies[0]); // Initial value is the first element of the array
    }

    private renderPolicyGrid(): JSX.Element {
        const policies = this.state.policies;
        const expiredPolicies = this.state.expiredPolicies?.filter(p => !this.state.policies!.some(po => po.policyNumber === p.policyNumber && po.annualPolicyInfo?.inGracePeriod));
        const hasExpiredPolicies = this.state.expiredPolicies != null && this.state.expiredPolicies.length > 0;
        const annualPolicies: Policy[] = policies!
            .filter(policy => (policy.products.includes(PRODUCT_WOLF_MOTOR) || policy.products.includes(PRODUCT_ANNUAL_HOME)) && !expiredPolicies?.some(p => p.policyNumber === policy.policyNumber))
            .sort((a, b) => moment(a.endDate).diff(b.endDate));
        const hasRenewedAnnualPolicies: boolean = annualPolicies.some((p: Policy) => p.annualPolicyInfo?.renewedPolicy);
        const motorPolicies: Policy[] = this.state.policies!.filter(policy => policy.products.includes(PRODUCT_MOTOR));
        const rentersPolicies: Policy[] = this.state.policies!.filter(policy => policy.products.includes(PRODUCT_RENTERS));
        const subscriptionPoliciesPresent: boolean = motorPolicies.length > 0 || rentersPolicies.length > 0;
        const wolfMotorPolicies: Policy[] = annualPolicies.filter(policy => policy.products.includes(PRODUCT_WOLF_MOTOR));
        const wolfRentersPolicies: Policy[] = annualPolicies.filter(policy => policy.products.includes(PRODUCT_ANNUAL_HOME));

        return (
            <>
                {this.renderPaymentOverdueBanners(annualPolicies)}
                <p className="mb-1 white-text active-policies-heading margin-bottom-32">Active policies</p>
                <div className="link-grid mt-0">
                    {this.renderAnnualPolicies(annualPolicies)}
                    {subscriptionPoliciesPresent && <div className="panel policy-panel">
                        <div className="row height-auto" onClick={() => this.navigateToSubscriptionDashboard()}>
                            <div className="col-1 mr-4">
                                <img className="vehicle-icon mt-5" src="/img/monthly-subscription-icon.png"
                                     alt="Subscription icon"/>
                            </div>
                            <div className="col-9">
                                <p className="font-weight-semi-bold text-blue-1 mb-0">Subscription(s)</p>
                                <p className="mb-0 mt-1">Renters and Car cover on monthly subscription</p>
                                <p className="mb-0 mt-1">Cancel anytime</p>
                            </div>
                        </div>
                    </div>}
                </div>
                <div>
                    {this.renderMenuTiles(hasExpiredPolicies, hasRenewedAnnualPolicies, policies![0])}
                </div>
                {wolfRentersPolicies.length === 0 && wolfMotorPolicies.length > 1 && wolfMotorPolicies[0].interaction.policyHolder.ownsHome
                    && <QuickQuoteBanner
                        policyHolder={wolfMotorPolicies[0].policyHolder}
                        policy={wolfMotorPolicies[0]}
                        {...this.props}/>}
            </>
        );
    }

    private renderAnnualPolicies(policies: Policy[]): JSX.Element[] {
        let policiesButtons: JSX.Element[] = [];

        const carPolicies = policies.filter(p => p.products.includes(PRODUCT_WOLF_MOTOR));
        const homePolicies = policies.filter(p => p.products.includes(PRODUCT_ANNUAL_HOME));

        policiesButtons = policiesButtons.concat(this.renderAnnualPolicy(carPolicies), this.renderAnnualPolicy(homePolicies));

        return policiesButtons;
    }

    private renderAnnualPolicy(policies: Policy[]): JSX.Element[] {
        const policiesButtons: JSX.Element[] = [];
        policies.forEach((policy, index) => {
            policiesButtons.push(
                <div key={policy.policyNumber} className="panel policy-panel">
                    <div className="d-flex p-0">
                        {policy.products.includes(PRODUCT_WOLF_MOTOR) && this.renderAnnualMotorPolicy(policy, index)}
                        {policy.products.includes(PRODUCT_ANNUAL_HOME) && this.renderAnnualHomePolicy(policy, index)}
                    </div>
                </div>
            )
        });
        return policiesButtons;
    }

    private renderPaymentOverdueBanners(policies: Policy[]): JSX.Element[] {
        const banners: JSX.Element[] = [];
        policies.forEach((policy) => {
            banners.push(this.renderPaymentOverdueBanner(policy))
        });
        return banners;
    }

    private renderPaymentOverdueBanner(policy: Policy): JSX.Element {
        const hasDebt = policy.annualPolicyInfo?.hasDebt ?? false;
        const isMotor = policy.products.includes(PRODUCT_WOLF_MOTOR);
        const propertyName = isMotor ? this.getVehicleName(policy) : this.getAddress(policy.insuredRealEstates[0].address);

        let reuseCard:boolean = false;
        this.customerPortalService.getPolicyPropertyByPropertyKey(policy.policyNumber, PolicyPropertyEnum.REUSE_MAIN_CARD).subscribe((property: PolicyProperty) => {
            reuseCard = property.value === 'true';
        });


        if (hasDebt) {
            return <div className="policy-panel-warning">
                    {
                        hasDebt &&
                        (policy.annualPolicyInfo?.overdueMonthlyInvoiceAmount != null && +policy.annualPolicyInfo.overdueMonthlyInvoiceAmount > 0
                            ? <OverdueInstalmentBanner amount={policy.annualPolicyInfo.overdueMonthlyInvoiceAmount}
                                                       unpaidInstallmentIdToAmount={policy.annualPolicyInfo.unpaidInstallmentIdToAmount}
                                                       multiProductPage={true}
                                                       policyNumber={policy.policyNumber}
                                                       product={policy.interaction.product}
                                                       propertyName={propertyName}
                                                        reuseCard={reuseCard}/>
                            : <OtherMissedPaymentBanner policyNumber={policy.policyNumber}/>)

                    }
                </div>
        }
        return <></>;
    }

    private getVehicleName(policy: Policy): string {
        const vehicle: any = policy.interaction.vehicles[0];
        return vehicle?.make + vehicle?.model + '(' + vehicle?.registrationNumber + ')';
    }

    private renderAnnualMotorPolicy(policy: Policy, index: number): React.ReactNode {
        const insuredVehicle: any = policy.insuredVehicles[0];
        const redirectUrl: string = `/dashboard/annualMotor/${policy.policyNumber}`;

        return (
            <>
                <div className={`float-left icon-number-${index + 1}`} onClick={() => this.navigateToPolicyDashboard(redirectUrl)}>
                    <img className="vehicle-icon icon-car-replace" src={`/img/annual/motor/Flow-car-${index + 1}.png`}
                         alt="Vehicle icon"/>
                </div>
                <div className="d-flex overflow-hidden flex-column"
                     onClick={() => this.navigateToPolicyDashboard(redirectUrl)}>
                    <p className="font-weight-semi-bold text-blue-1 mb-0 policy-address">Car policy</p>
                    <div className="number-plate-preview margin-top-10 margin-bottom-10">{insuredVehicle.registrationNumber}</div>
                    <p className="mb-0">{insuredVehicle.make} {insuredVehicle.model}</p>
                    <p className="mb-0 policy-expiration">Cover ends on {formatDateTime(policy.endDate)}</p>
                </div>
            </>
        )
    }

    private renderAnnualHomePolicy(policy: Policy, index: number): React.ReactNode {
        const redirectUrl: string = `/dashboard/annualHome/${policy.policyNumber}`;

        return (
            <>
                <div className={`float-left icon-number-${index + 1}`} onClick={() => this.navigateToPolicyDashboard(redirectUrl)}>
                    <img className="vehicle-icon icon-home-replace" src={`/img/annual/home/Flow-home-${index + 1}.png`}
                         alt="Vehicle icon"/>
                </div>
                <div className="d-flex overflow-hidden flex-column"
                     onClick={() => this.navigateToPolicyDashboard(redirectUrl)}>
                    <p className="font-weight-semi-bold text-blue-1 mb-0 policy-address">Home policy</p>
                    <p className="d-flex mb-0 home-address margin-bottom-10">
                        <p className="policy-address">{this.getAddress(policy.insuredRealEstates[0].address)},&nbsp;</p>
                        <p className="policy-postal-code">{policy.insuredRealEstates[0].address.postalCode}</p>
                    </p>
                    <p className="mb-0 policy-expiration">
                        Buildings and contents<br/>
                        Cover ends on {formatDateTime(policy.endDate)}
                    </p>
                </div>
            </>
        )
    }

    private renderMenuTiles(hasExpiredPolicies: boolean, hasOldPolicies?: boolean, policy?: any) {
        return <>
            <p className="mb-1 mt-5 menu-heading margin-bottom-32">Menu</p>
            <div className="d-flex flex-wrap menu-items">
                <div className="flexItem quotes">
                    <ImgTileLink id="getQuoteTile"
                                 src={`/${resourcesHelper.THEME}/img/savings_icon.png`}
                                 alt="Products icon"
                                 to={`/dashboard/getNewQuote`}
                                 routeState={{
                                     policy
                                 }}
                                 {...this.props}>
                        Our products
                    </ImgTileLink>
                </div>

                <div className="flexItem account-details">
                    <ImgTileLink src={`/${resourcesHelper.THEME}/img/account-details.png`} alt="Account details"
                                 to={"/dashboard/multiPolicy/account"}
                                 {...this.props}>
                        Account details
                    </ImgTileLink>
                </div>

                <div className="flexItem marketing-preferences">
                    <ImgTileLink src={`/${resourcesHelper.THEME}/img/exclamation-mark-dark.svg`} alt="Marketing preferences"
                                 to={"/dashboard/marketing-preferences"}
                                 {...this.props}>
                        Marketing preferences
                    </ImgTileLink>
                </div>

                {(hasExpiredPolicies || hasOldPolicies) &&
                    (<div className="flexItem p-0 col old-policies">
                            <ImgTileLink src={`/${resourcesHelper.THEME}/img/old-policies.png`} alt="Old policies"
                                         to={"/dashboard/annualOldPolicies"}
                                         {...this.props}>
                                Old policies
                            </ImgTileLink>
                        </div>
                    )}

                {this.state.multiPolicyAccount && <div className="flexItem savings">
                    <ImgTileLink
                        src={`/${resourcesHelper.THEME}/img/gift_icon.svg`}
                        alt="Offers icon"
                        to={`https://www.allianz.co.uk/insurance/car-insurance/customer-offers`}
                        hasInfoButton={true}
                        infoModalTitle="My offers"
                        infoModalBulletPoints={["Save with exclusive offers for Allianz customers"]}
                        infoModalBulletPointTitle="Our way of saying thanks"
                        openInNewWindow={true}
                        {...this.props}
                    >
                        My offers
                    </ImgTileLink>
                </div>}

            </div>
        </>;
    }

    private renderNewClientPageContents(): JSX.Element {
        // @ts-ignore
        const {party} = this.state;
        return (
            <React.Fragment>
                <div className="banner wide-gutters banner-info">
                    <div className="banner-text">
                        While you do not have an active insurance policy with us we will keep your details safe and
                        ready for when you need us
                    </div>
                </div>
                <div className="new-client-panel">
                    <img src="/img/Flow-empty.svg" alt="Emptiness"/>
                    <div className="new-client-panel-heading">It's empty around here...</div>
                    <ButtonLink id="quote" primary={true} to="/products">
                        Get a Allianz quote
                    </ButtonLink>
                </div>
                <div className="link-grid">
                    <div className="row no-gutters">
                        <div className="col-6 email">
                            <ImgTileLink src="/img/email-dark.svg" alt="Account details" to="#"
                                         {...this.props}>
                                <p className="title-panel long-word d-inline-block">
                                    {`${party ? ` ${party.email}` : ""}`}
                                </p>
                            </ImgTileLink>
                        </div>
                        <div className="col-6 quotes tile">
                            <ImgTileLink src={`/${resourcesHelper.THEME}/img/Flow-quotes.svg`} alt="Quotes"
                                         to="/dashboard/viewOrGetQuotes"
                                         {...this.props}>
                                View / get new quotes
                            </ImgTileLink>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    private redirectToSingleProductDashboard(redirectUrl: string): React.ReactNode {
        return <Redirect to={redirectUrl}/>
    }

    private navigateToPolicyDashboard = (redirectUrl: string) => {
        window.location.assign(redirectUrl);
    }

    private navigateToSubscriptionDashboard = () => {
        window.location.assign(`/dashboard/monthlySubscription`);
    }

    private isDataLoaded = (): boolean => {
        return !!this.state.policies
            && this.state.policiesProcessed
            && this.state.partyLoaded;
    };

    private hasPolicies = (): boolean => {
        return !!this.state.policies && this.state.policies?.length !== 0;
    };

    private getAddress(address: any): string {
        const addressLine1: string = address.addressLine1 ? address.addressLine1 + ", " : "";
        const addressLine2: string = address.addressLine2 ? address.addressLine2 + ", " : "";
        const addressLine3: string = address.addressLine3 ? address.addressLine3 + ", " : "";
        const addressLine4: string = address.addressLine4 ? address.addressLine4 + ", " : "";

        return capitalize(addressLine1 + addressLine2 + addressLine3 + addressLine4).replace(/\s*,/g, ",").trim();
    }
}



// WEBPACK FOOTER //
// ./src/pages/dashboard/common/MultiPoliciesDashboardPage.tsx